import { usePage } from "@inertiajs/react";
import JulesReading from "components/svgs/jules/JulesReading";
import Badge from "hardcover-ui/components/Badge";
import Heading from "hardcover-ui/components/Heading";
import Text from "hardcover-ui/components/Text";
import { UserStatsPageProps } from "pages/clientverse/users/stats/index.props";
import React from "react";
import { useChartTheme } from "../contexts/ChartThemeContext";
import StatWrapper from "../shared/StatWrapper";
import { useStatPeriod } from "../StatPeriodContext";

interface LibraryBreakdownStatProps {
  size?: "sm" | "md" | "lg" | "xl";
}

const MAX_BOOKS = 25;

const LibraryBreakdownStat: React.FC<LibraryBreakdownStatProps> = ({
  size,
}) => {
  const { selectedPeriod } = useStatPeriod();
  const { user, isOwner, stats } = usePage<UserStatsPageProps>().props;
  const { libraryBreakdowns: data } = stats;
  const { theme } = useChartTheme();

  if (!data || data.length === 0) {
    return (
      <StatWrapper title="Library Breakdown" size={size}>
        <div className="flex flex-col h-max w-full relative pb-16">
          <Heading size="sm" className="mb-2" level={3}>
            {isOwner ? "Your" : `${user.name}'s`} library data is not available
          </Heading>
        </div>
      </StatWrapper>
    );
  }

  const colors = {
    "Want to Read":
      theme === "colorful"
        ? ["var(--chart-colorful-4)", "var(--chart-colorful-4)"]
        : ["var(--chart-mono-7)", "var(--chart-mono-8)"],
    Read:
      theme === "colorful"
        ? ["var(--chart-colorful-5)", "var(--chart-colorful-5)"]
        : ["var(--chart-mono-1)", "var(--chart-mono-3)"],
    Stopped:
      theme === "colorful"
        ? ["var(--chart-colorful-7)", "var(--chart-colorful-8)"]
        : ["var(--chart-mono-10)", "var(--chart-mono-9)"],
  };

  const coloredData = [...data].map((item) => ({
    ...item,
    value: item.value || 0,
    colors: colors[item.label] || colors["Want to Read"],
  }));

  const wantToReadItem = data.find((item) => item.label === "Want to Read");
  const readItem = data.find((item) => item.label === "Read");

  const booksReadFromWantToRead = readItem?.value || 0;
  const wantToReadCount = wantToReadItem?.value || 0;
  const percentageRead =
    wantToReadCount + booksReadFromWantToRead > 0
      ? (
          (booksReadFromWantToRead /
            (wantToReadCount + booksReadFromWantToRead)) *
          100
        ).toFixed(1)
      : "0.0";

  const renderBookBar = (item: (typeof coloredData)[0]) => {
    const bookCount = Math.min(item.value, MAX_BOOKS);
    const books = Array.from({ length: bookCount }, (_, i) => (
      <div
        key={i}
        className="w-4 h-6 rounded-xs"
        style={{
          background: `linear-gradient(to right, ${item.colors[0]}, ${item.colors[1]})`,
        }}
      />
    ));

    return (
      <div className="flex-1 flex items-center gap-1">
        {books}
        {item.value > MAX_BOOKS && (
          <Text variant="secondary" size="sm">
            +{item.value - MAX_BOOKS}
          </Text>
        )}
      </div>
    );
  };

  return (
    <StatWrapper title="Library Breakdown" size={size}>
      <div className="flex flex-col h-max w-full relative pb-16">
        <Heading size="sm" className="mb-2" level={3}>
          {isOwner ? "Your" : `${user.name}'s`} library based on books added
          during this period
        </Heading>
        <div className="flex-1 flex flex-col justify-between gap-4 mb-4 w-full overflow-x-scroll no-scrollbar">
          {coloredData.map((item) => (
            <div key={item.label} className="flex items-center">
              <div className="w-32 flex flex-col shrink-0">
                <Text size="md">{item.label}</Text>
                <Text className="font-bold text-4xl">
                  {item.value.toLocaleString()}
                </Text>
              </div>
              {renderBookBar(item)}
            </div>
          ))}
        </div>
        <div className="absolute bottom-0 left-0 right-0 flex justify-between items-end">
          <Text size="sm" className="text-tertiary">
            You've read <Badge variant="secondary">{percentageRead}%</Badge> of
            your Want to Read list added during this {selectedPeriod}.
          </Text>
          <JulesReading className="w-24 mix-blend-luminosity h-auto -mb-4" />
        </div>
      </div>
    </StatWrapper>
  );
};

export default LibraryBreakdownStat;
